<template>     
    <div v-if="!canReadProjects" class="area-content-body">
        <div class="no-permission-message">
            Sie haben keine Berechtigung, die Projektübersicht zu öffnen.
        </div>
    </div>  
    <div v-else class="area-content-body projects-root" :class="rootClasses">
        <DxDataGrid v-show="!mapView||mapView=='split'"
            id="projectRootGrid"
            ref="grid"
            :remoteOperations="true"
            :stateStoring="gridStateStoring"
            keyExpr="_id"
            :focused-row-key="focusedRowKey"
            :focused-row-enabled="true"
            :auto-navigate-to-focused-row="autoNavigateToFocusedRow"
            :allow-column-reordering="true"
            :sync-lookup-filter-values="false"
            :columnChooser="gridColumnChooser"
            @cellPrepared="(e)=>{if(e.column.dataField =='active'){ e.cellElement.classList.add('cell-'+e.value) } }"
            :scrolling="gridScrolling"
            :data-source="shownProjects"
            :allow-column-resizing="true"
            column-resizing-mode="nextColumn"
            @rowDblClick="changeProject">

            <DxToolbar>
                <DxItem name="columnChooserButton" location="after" />
                <DxItem name="searchPanel" location="after" />                
                <DxItem location="after">
                    <template #default>
                        <DxSelectBox v-if="mapView" style="margin-right:1rem;" width="300" :data-source="mapSearchResults" display-expr="formattedAddress" v-model:value="mapSearchQuery" :search-timeout="200" :show-data-before-search="false" :search-enabled="true" placeholder="Karten suche..." mode="search" :show-clear-button="true" @value-changed="handleMapSearch" />
                    </template>
                </DxItem>
            </DxToolbar>

            <DxColumn data-field="number" caption="Projekt-Nr." :sort-index="0" sort-order="asc" />
            <DxColumn data-field="name" caption="Projekt-Bezeichnung" />
            <DxColumn data-field="clientId" cell-template="contactTemplate" caption="Auftraggeber" :allow-editing="false" />
            <DxColumn data-field="clientOrganizationId" cell-template="organizationTemplate" caption="Auftraggeber Firma" :allow-editing="false" />
            <DxColumn data-field="coordinatorId" cell-template="userTemplate" caption="Koordinator" :allow-editing="false" />

            <DxColumn data-field="status" caption="Status" :lookup="{dataSource:$root.getLookupDs('status'),valueExpr:'id',displayExpr:'name'}"></DxColumn>

            <template #contactTemplate="{ data }">
                <ContactLookup v-if="data.value" :value="data.value" :view="true"></ContactLookup>
                <div v-else></div>
            </template>

            <template #organizationTemplate="{ data }">
                <OrganizationLookup v-if="data.value" :value="data.value" :view="true"></OrganizationLookup>
                <div v-else></div>
            </template>

            <template #userTemplate="{ data }">
                <UserLookup v-if="data.value" :value="data.value" :view="true"></UserLookup>
                <div v-else></div>
            </template>
            <DxSearchPanel :visible="true" width="300" placeholder="Volltext Suche..." />
            <DxFilterRow :visible="true" />

            <DxColumn data-field="start" data-type="date" format="dd.MM.yyyy" caption="Start" :allow-editing="false" :visible="false" />
            <DxColumn data-field="end" data-type="date" format="dd.MM.yyyy" caption="Ende" :allow-editing="false" :visible="false" />
            <DxColumn data-field="streetAndNr" caption="Straße und Hausnummer" :allow-editing="false" :visible="false" />
            <DxColumn data-field="zipCode" caption="PLZ" :allow-editing="false" :visible="false" />
            <DxColumn data-field="city" caption="Stadt" :allow-editing="false" :visible="false" />
            <DxColumn data-field="shortDescription" caption="Kurze Beschreibung" :allow-editing="false" :visible="false" />
            <DxColumn data-field="longDescription" caption="Lange Beschreibung" :allow-editing="false" :visible="false" />
            <DxColumn data-field="lastChanged" caption="Letzte Änderung" format="dd.MM.yyyy" :allow-editing="false" :visible="false" />
            <DxColumn data-field="buildingOwnerId" cell-template="contactTemplate" caption="Bauherrschaft" :allow-editing="false" :visible="false" />
            <DxColumn data-field="buildingOwnerOrganizationId" cell-template="organizationTemplate" caption="Bauherrschaft Firma" :allow-editing="false" :visible="false" />
            <DxColumn data-field="architectId" cell-template="contactTemplate" caption="Architekt" :allow-editing="false" :visible="false" />
            <DxColumn data-field="architectOrganizationId" cell-template="organizationTemplate" caption="Architekt Firma" :allow-editing="false" :visible="false" />
            <DxColumn data-field="substituteCoordinatorId" cell-template="userTemplate" caption="Stellvertretender Koordinator" :allow-editing="false" :visible="false" />
            <DxColumn data-field="projectManagerId" cell-template="userTemplate" caption="Projektleiter" :allow-editing="false" :visible="false" />
            <DxColumn data-field="internalDescription" caption="Interne Beschreibung" :allow-editing="false" :visible="false" />
            <DxColumn data-field="active" caption="Aktiv" :allow-editing="false" :visible="false" />
            <DxColumn data-field="archived" caption="Archiviert" :allow-editing="false" :visible="false" />
            <DxColumn :visible="mapView" cell-template="actionTemplate" caption="Aktion" :allow-editing="false" />
            <template #actionTemplate="{ data }">
                <span v-if="data.data.locationLat && data.data.locationLng" icon="map" @click.prevent.stop="zoomTo(data)" class="action-button material-symbols-outlined">location_pin</span>
            </template>
        </DxDataGrid>
        <map-component ref="map"
            v-if="initMap"
            v-show="showMap"
            @project-changed="shownProjects.reload();"
            :query="currentQuery" />
        <div v-else></div>
        <div v-if="loading" class="no-data-message"></div>
    </div>
</template>

<script lang="js">
    import { defineComponent, watchEffect } from 'vue';
    import DxDataGrid, {
        DxColumn,
        DxSearchPanel,
        DxFilterRow,
        DxToolbar,
        DxItem
    } from 'devextreme-vue/data-grid';
    import ContactLookup from '../components/contact-lookup.vue';
    import UserLookup from '../components/user-lookup.vue';
    import OrganizationLookup from '../components/organization-lookup.vue';
    import projectApi from '../services/ProjectApi';
    import 'devextreme/data/array_store';
    import listApi from '../services/ListApi';
    import DataSource from 'devextreme/data/data_source';
    import { luceneConverter } from '../services/Util';
    import MapComponent from '../components/map-component.vue'
    import meApi from '../services/MeApi';
    import AppPermissions from '../constants/AppPermissions';

    import mapApi from '../services/MapApi';
    import { DxSelectBox } from 'devextreme-vue';
import CustomStore from 'devextreme/data/custom_store';
    export default defineComponent({
        name: "ProjectsRoot",
        components: {
            DxDataGrid,
            DxSelectBox,
            DxColumn,
            DxToolbar,
            DxItem,
            ContactLookup,
            OrganizationLookup,
            UserLookup,
            DxSearchPanel,
            DxFilterRow,
            MapComponent
        },
        props: {
            mapView: String
        },
        data() {
            return {
                mapSearchResults: new CustomStore({
                    byKey() {
                        return null
                    },
                    load: (loadOptions) => {
                        const promise = new Promise((resolve) => {
                            console.log("loadOptions", loadOptions);
                            const searchQuery = loadOptions.searchValue;
                            console.log("SEARCH QUERY:", searchQuery);
                            if (!searchQuery) {
                                resolve([]);
                            }
                            mapApi.search(searchQuery).then(data => {
                                console.log("SEARCH RESULTS:", data);
                                resolve(data || []);
                            }).catch(error => {
                                console.error(error)
                                resolve([]);
                            });             
                        });
                        return promise;
                    },
                }),
                gridStateStoring: this.getStateStoring(),
                gridColumnChooser: { enabled: true },
                gridScrolling: { mode: 'virtual', preloadEnabled: true },
                loading: false,
                autoNavigateToFocusedRow: false,
                projectCount: -1,
                shownProjects: null,
                showArchived: false,
                focusedRowKey: null,
                currentQuery: null,
                random: null,
                initMap: false,
                mapSearchQuery: ''
            };
        },
        computed: {
            canReadProjects() {
                return meApi.hasPermissionSync(AppPermissions.ProjectsOverview.Read);
            },
            canReadMap() {
                return meApi.hasPermissionSync(AppPermissions.Map.Read);
            },
            showGrid() {
                return !this.mapView || this.mapView === 'split';
            },
            showMap() {
                return this.canReadMap && (this.mapView === 'split' || this.mapView === 'full');
            },
            rootClasses() {
                return {
                    'projects-root-split': this.mapView === 'split' && this.canReadMap,
                    'projects-root-full': this.mapView === 'full' && this.canReadMap
                };
            }
        },
        created() {
            if (!this.canReadProjects)
                return;

            this.initMap = this.canReadMap && (this.mapView == 'split' || this.mapView == 'full');

            console.log("[INFO] Api-URL:", process.env.VUE_APP_API_URL);

            this.shownProjects = new DataSource({
                load: async (loadOptions) => {
                    let sort = loadOptions.sort
                        ? loadOptions.sort.filter(s => s.selector != "_id").map(s => (s.desc ? "!" : "") + luceneConverter.toPascalCase(s.selector)).join(",")
                        : "";

                    if (loadOptions.filter) {
                        let query = luceneConverter.toLucene(loadOptions.filter);
                        this.currentQuery = query;
                        let result = await projectApi.search(`Archived:${(this.showArchived ? "1" : "0")} AND (${query})`, loadOptions.take, loadOptions.skip, sort)
                        this.projectCount = (result.data.length == 0 && loadOptions.skip == 0) ? 0 : result.totalHits;
                        return {
                            data: result.data, totalCount: result.totalHits
                        };
                    } else {
                        this.currentQuery = "_id:*";
                        let result = await projectApi.search(`Archived:${(this.showArchived ? "1" : "0")}`, Math.min(100, loadOptions.take), loadOptions.skip, sort);
                        this.projectCount = (result.data.length == 0 && loadOptions.skip == 0) ? 0 : result.totalHits;
                        return {
                            data: result.data, totalCount: result.totalHits
                        }
                    }
                },
                byKey: async (key) => {
                    try {
                        var project = await projectApi.get(key);
                        if (!project) {
                            console.error("project not found", key);
                        }
                        return project;
                    }
                    catch (ex) {
                        console.error("error while fetching project by key", key, ex);
                        return null;
                    }
                },
                key: "_id"
            });
        },
        mounted() {
            if (!this.canReadProjects)
                return;

            watchEffect(async () => {
                if (this.$root.$refs.head?.setArchived) {
                    this.$root.$refs.head.setArchived = false;
                    let key = this.$refs.grid?.instance?.option("focusedRowKey");
                    let project = await projectApi.get(key)
                    if (project) {
                        project.archived = !project.archived;
                        projectApi.save(project);
                        this.updateFilter();
                    }
                }
            })

            watchEffect(() => {
                if (this.showArchived == this.$root.$refs.head.showArchived)
                    return;
                this.showArchived = this.$root.$refs.head.showArchived
                this.updateFilter();
            })
        },
        watch: {
            '$route.params': "routeChanged",
            mapView(v, o) {
                if (!this.canReadMap)
                    return;

                if (!this.initMap) {
                    this.initMap = this.mapView == 'split' || this.mapView == 'full'
                }
                if (!o && v) {
                    if (!this.$refs.map) {
                        this.$nextTick(() => {
                            this.$refs.map.updateProjects();
                        })
                    }
                }
            }
        },
        methods: {
            zoomTo(row) {
                this.$refs.map.hasFocus = false;
                this.$refs.map.zoomOn({
                    lng: row.data.locationLng,
                    lat: row.data.locationLat
                })
            },
            async handleMapSearch(address) {
                console.log("address", address)
                if (address.value) {
                    this.$refs.map.hasFocus = false;
                    this.$refs.map.zoomOn(address.value.location)
                }                
            },
            changeProject(row) {
                this.$router.push("/Projects/" + row.data._id);
                if (this.$refs.map) {
                    console.log("row.data", row.data)
                    // this.$refs.map.zoomOn()
                }
            },
            updateFilter() {
                if (!this.shownProjects)
                    return;
                this.shownProjects.reload()
            },
            getStateStoring() {
                let key = "project-grid:" + this.$root.currentUser;
                return {
                    enabled: true,
                    type: "custom",
                    async customLoad() {
                        let settingsList = await listApi.getByName("sys_settings");
                        let item = await listApi.getListItemByValue(settingsList._id, key);
                        if (item)
                            return JSON.parse(item.data);
                        return null;
                    },
                    async customSave(e) {
                        let settingsList = await listApi.getByName("sys_settings");
                        let item = await listApi.getListItemByValue(settingsList._id, key);
                        if (item) {
                            let prev = item.data
                            item.data = JSON.stringify({ columns: e.columns });
                            if (prev == item.data) {
                                return item;
                            }
                            await listApi.saveItem(item);
                        }
                        else {
                            return await listApi.saveItem({ listId: settingsList._id, value: key, data: JSON.stringify({ columns: e.columns }) });
                        }
                    }
                }
            },
            routeChanged() {
            },
            async fetchData() {
                if (!this.canReadProjects)
                    return;

                this.projectCount = -1;
                this.$root.project = null;
                this.loading = true;
                this.$root.fileDirectory = null;
                this.showArchived = this.$root.$refs.head && this.$root.$refs.head.showArchived;
                this.updateFilter();
                this.loading = false;
            },
            test() {
                this.$router.push("/MainData")
            }
        },
    });
</script>

<style>
    .projects-root {
        background-color: var(--color-dx-dark);
        display: grid;
        grid-template-columns: 1fr 0;
        grid-template-rows: minmax(0, 1fr);
    }

    .projects-root-full {
        grid-template-columns: 1fr;
    }

    .projects-root-split {
        grid-template-columns: 1fr 1fr;
    }
    .action-button {
        outline-offset: 2px;
        outline: 1px solid white;
        border-radius: 2px;
        position: absolute;
        margin-top: -1px;
        font-size: 20px;
    }
    .action-button:hover {
        background: rgba(255,255,255,0.3);
    }
    div#projectRootGrid {
        height: 100%;
    }

    .hide-element {
        display: none;
    }

    .no-permission-message {
        padding: 20px;
        background-color: #2a2a2a;
    }
</style>